import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../supabase/supabaseClient";
import "../style/homepage.css";
import Footer from "../components/Footer";
import Header from "../components/Header";

function Homepage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCars = useCallback(async () => {
    const { data, error } = await supabase
      .from("cars")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Supabase cars error:", error.message);
      setLoading(false);
      return;
    }

    setCars(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCars();

    const channel = supabase
      .channel("homepage-cars-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cars" },
        () => { fetchCars(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchCars]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    const targets = document.querySelectorAll(".reveal");
    targets.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [cars]);

  return (
    <div className="hp-root">
      <Header />

      {/* ── HERO ───────────────────────────────────── */}
      <section className="hp-hero">
        <div className="hp-hero__content">
          <div className="hp-hero__accent" />
          <h1 className="hp-hero__title">
            {t("home.heroTitleStart")}{" "}
            <em className="hp-hero__title-em">{t("home.heroTitleHighlight")}</em>
          </h1>
          <button
            className="hp-btn hp-btn--gold"
            onClick={() => navigate("/cars")}
          >
            {t("home.browseInventory")}
          </button>
        </div>

        <div className="hp-hero__scroll">
          <span className="hp-hero__scroll-line" />
        </div>
      </section>

      {/* ── FEATURED ───────────────────────────────── */}
      <section className="hp-featured">
        <div className="hp-featured__head reveal">
          <h2 className="hp-section-title">{t("home.featuredTitle")}</h2>
          <p className="hp-section-sub">{t("home.featuredText")}</p>
        </div>

        {loading ? (
          <p className="hp-msg">{t("home.loadingCars")}</p>
        ) : cars.length === 0 ? (
          <p className="hp-msg">{t("home.noCars")}</p>
        ) : (
          <div className="hp-cars">
            {cars.slice(0, 3).map((car, i) => {
              const carImage =
                car.image_url ||
                car.image ||
                car.main_image ||
                car.photo_url ||
                car.photos?.[0] ||
                car.images?.[0] ||
                "https://via.placeholder.com/800x500?text=No+Image";

              return (
                <div
                  className="hp-car reveal"
                  key={car.id}
                  style={{ transitionDelay: `${i * 0.13}s` }}
                >
                  <div className="hp-car__img-wrap">
                    <img
                      src={carImage}
                      alt={car.car_name || car.name || car.model || "Car"}
                      className="hp-car__img"
                    />
                    <div className="hp-car__tag">
                      <span className="hp-car__dot" />
                      {t("home.available")}
                    </div>
                  </div>

                  <div className="hp-car__body">
                    <div className="hp-car__row">
                      <h3 className="hp-car__name">
                        {car.car_name || car.name || car.model || t("home.unnamedCar")}
                      </h3>
                      <span className="hp-car__year">{car.year || "N/A"}</span>
                    </div>

                    <p className="hp-car__brand">
                      {car.brand || car.make || t("home.unknownBrand")}
                    </p>

                    <div className="hp-car__foot">
                      <span className="hp-car__price">
                        {car.price
                          ? `€${Number(car.price).toLocaleString()}`
                          : t("home.priceRequest")}
                      </span>
                      <button
                        className="hp-btn hp-btn--dark"
                        onClick={() => navigate(`/cars/${car.id}`)}
                      >
                        {t("home.viewDetails")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── WHY US ─────────────────────────────────── */}
      <section className="hp-why">
        <div className="hp-why__inner">
          <div className="hp-why__head reveal">
            <h2 className="hp-section-title hp-section-title--light">
              {t("home.whyTitle")}
            </h2>
            <p className="hp-why__sub">{t("home.whyText")}</p>
          </div>

          <div className="hp-why__grid">
            {[
              {
                num: "01",
                icon: "🏅",
                title: t("home.qualityTitle"),
                text: t("home.qualityText"),
              },
              {
                num: "02",
                icon: "🛡️",
                title: t("home.trustedTitle"),
                text: t("home.trustedText"),
              },
              {
                num: "03",
                icon: "⚡",
                title: t("home.fastTitle"),
                text: t("home.fastText"),
              },
            ].map((item, i) => (
              <div
                className="hp-why__card reveal"
                key={item.num}
                style={{ transitionDelay: `${i * 0.15}s` }}
              >
                <span className="hp-why__num">{item.num}</span>
                <div className="hp-why__icon">{item.icon}</div>
                <h3 className="hp-why__card-title">{item.title}</h3>
                <p className="hp-why__card-text">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────── */}
      <section className="hp-cta">
        <div className="hp-cta__inner reveal">
          <h2 className="hp-cta__title">{t("home.ctaTitle")}</h2>
          <p className="hp-cta__text">{t("home.ctaText")}</p>
          <button
            className="hp-btn hp-btn--gold"
            onClick={() => navigate("/contact")}
          >
            {t("home.contactButton")} →
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Homepage;
